import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs';
import { AppEpic } from 'src/store';
import { of } from 'rxjs';
import { updateFlowStatus } from 'src/components/Subheader/Subheader.slice';
import { updateAggBys, fetchNestedAttributeData } from './NestedAttribute.slice';
import {
  inputIsNotNullOrUndefined,
  isScopeDefined,
  maybeGetComponentProps,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { ConfDefnComponentType, NestedAttributeComponent } from 'configuration-schemas/confdefnComponents';
import { DataApi, isListDataApi } from 'configuration-schemas/confdefnView';
import {
  ExtraPivotOptions,
  getConfigureModalAggBys,
  organizeListDataOptions,
} from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { LEVEL_PROD_ROOT } from 'src/utils/Domain/Constants';
import {
  receiveScopeConfig,
  receiveScopeRefreshTrigger,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';
import { isNotBlacklistedByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import { defaultsToAggBys } from 'src/utils/Component/Configure';
import { isEmpty } from 'lodash';

export const nestedAttributeLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      // This view's initial navigation data load is not triggered by setActivePage
      // because the updateAggBys action is triggered in the onShowView container method
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      updateFlowStatus.type,
      updateAggBys.type,
      receiveScopeConfig.type,
      updateSelectedItem.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() => maybeGetComponentProps<NestedAttributeComponent>(state$.value, ConfDefnComponentType.nestedAttribute)),
    filter(inputIsNotNullOrUndefined),
    filter(() => isScopeDefined(state$.value.scope)),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => state$.value.pages.hindsighting.aggregateViews.nestedAttribute.aggBys.length > 0),
    mergeMap(({ defns, topMembers }) => {
      const aggBys = state$.value.pages.hindsighting.aggregateViews.nestedAttribute.aggBys;
      const flowStatus = state$.value.subheader.flowStatus.join(',');

      const { model: modelDefn } = defns;
      // TODO: move this to config when ready
      const dataApi: DataApi = {
        defnId: modelDefn || defns.view[0],
        isListData: true,
        params: {
          aggBy: '', // no bottom aggBy currently needed for this view
          topAggBy: LEVEL_PROD_ROOT,
          topMembers,
        },
      };
      const finalModelDefn = isListDataApi(dataApi) ? dataApi.defnId : modelDefn;

      const aggBy = getConfigureModalAggBys(aggBys, dataApi);
      const options: ExtraPivotOptions = { flowStatus, aggBy };
      const finalOptions = organizeListDataOptions(options, dataApi);

      return of(fetchNestedAttributeData(finalModelDefn, finalOptions));
    }),
  );
};

/** This is here to correct aggBys state if the user scopes to a department
 * in which one of the `aggBys` is blacklisted in configure.
 * It attempts to check for all the relevant config and keys and if an `aggBys`
 * is blacklisted, then just returns defaults again
 */
export const nestedAttributePruneBlacklistedAggBys: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(receiveScopeRefreshTrigger.type),
    map(() => {
      const { attributeBlacklist } = state$.value.subheader;
      const currentDepartment = state$.value.scope.scope.productMember;
      const { aggBys, viewDefns } = state$.value.pages.hindsighting.aggregateViews.nestedAttribute;
      return { attributeBlacklist, currentDepartment, aggBys, configure: viewDefns.configure };
    }),
    filter(
      ({ attributeBlacklist, currentDepartment, aggBys }) =>
        !isEmpty(attributeBlacklist) && !isEmpty(currentDepartment) && !isEmpty(aggBys),
    ),
    filter(({ attributeBlacklist, currentDepartment, aggBys }) => {
      if (!attributeBlacklist || !currentDepartment) return false;
      const keep = isNotBlacklistedByDataIndex(attributeBlacklist, currentDepartment);
      return aggBys.some((dataIndex) => !keep({ dataIndex }));
    }),
    map(({ configure }) => updateAggBys(defaultsToAggBys(configure))),
  );
};
